import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { digitalApi, errMsg } from '@/lib/digitalApi';
import { usePageMeta } from '@/lib/usePageMeta';

const STATE_COPY = {
  expired: 'This download link has expired.',
  limit_reached: 'This link has reached its download limit.',
  replaced: 'This link has been replaced by a newer one.',
  revoked: 'Access for this download is no longer active. Please contact us if you believe this is an error.',
};

const NewLinkForm = ({ code }) => {
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setMsg((await digitalApi.requestNewLink({ email, code })).message); } catch (err) { setMsg(errMsg(err)); }
    setBusy(false);
  };
  if (msg) return <p className="mt-8 text-matte/75" data-testid="digital-new-link-message">{msg}</p>;
  return (
    <form onSubmit={submit} className="mt-8 max-w-md" data-testid="digital-new-link-form">
      <p className="text-matte/70 text-sm mb-4">Enter the email you used, and we&rsquo;ll send a fresh secure link to that address.</p>
      <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
        className="w-full bg-transparent border-b border-taupe/40 py-3 focus:outline-none focus:border-gold" data-testid="digital-new-link-email" />
      <button type="submit" disabled={busy} className="btn-primary mt-6 disabled:opacity-60" data-testid="digital-new-link-submit">Send Me a New Link</button>
    </form>
  );
};

export default function DigitalDownload() {
  usePageMeta({ title: 'Your Download', path: '/download', robots: 'noindex' });
  const { code } = useParams();
  const [info, setInfo] = useState(undefined);
  const [clicked, setClicked] = useState(false);
  useEffect(() => { digitalApi.linkInfo(code).then(setInfo).catch(() => setInfo(null)); }, [code]);

  return (
    <div className="bg-ivory min-h-screen" data-testid="digital-download-page">
      <section className="pt-40 lg:pt-56 pb-24">
        <div className="mx-auto max-w-3xl px-6 sm:px-10">
          <div className="eyebrow !text-gold mb-8">ORIGENNE&trade; &middot; Your Download</div>
          {info === undefined && <p className="text-matte/50">Loading…</p>}
          {info === null && (
            <>
              <h1 className="font-serif-display text-5xl tracking-tighter text-matte">This link isn&rsquo;t valid.</h1>
              <p className="mt-6 text-matte/70" data-testid="digital-download-invalid">Please check the link in your email, or request a new one below.</p>
              <NewLinkForm />
            </>
          )}
          {info && (
            <>
              <h1 className="font-serif-display text-5xl md:text-6xl leading-[1] tracking-tighter text-matte break-words" data-testid="digital-download-title">{info.product_title}</h1>
              <div className="mt-4 text-[10px] font-label tracking-widest uppercase text-taupe">
                {[info.format, info.file_type].filter(Boolean).join(' · ')}
              </div>
              {info.state === 'active' ? (
                <>
                  <a href={digitalApi.fileUrl(code)} onClick={() => setClicked(true)} className="btn-primary mt-10" data-testid="digital-download-button">
                    <Download size={14} strokeWidth={1.6} /> Download
                  </a>
                  <p className="mt-6 text-sm text-matte/55" data-testid="digital-download-remaining">
                    {Math.max(info.downloads_remaining - (clicked ? 1 : 0), 0)} of {info.max_downloads} downloads remaining · Link valid until {new Date(info.expires_at).toLocaleDateString()}
                  </p>
                </>
              ) : (
                <>
                  <p className="mt-8 text-matte/75" data-testid="digital-download-state">{STATE_COPY[info.state]}</p>
                  {info.state !== 'revoked' && <NewLinkForm code={code} />}
                  {info.state === 'revoked' && <Link to="/contact" className="btn-primary mt-8">Contact Us</Link>}
                </>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
